import React from 'react';
import styled from 'styled-components';
import { JsonEditor } from 'json-edit-react';
import {
  createRfpTemplateSlideField,
  deleteRfpTemplateSlideField,
  fetchRfpTemplateSlideFields,
  updateRfpTemplateSlideField,
  DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD,
} from '@services/rfp';
import { Body5, Container, H5 } from '@ui';
import { CollapseButton, FlatButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { Working } from '@ui/Working';
import { suggestNextTemplateKey } from './suggestNextTemplateKey';

const SlideFieldCard = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  padding: 16px;
`;

const InputField = styled.input`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.text};
  background-color: transparent;
  border-radius: 6px;
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
`;

const TextareaField = styled.textarea`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.text};
  background-color: transparent;
  border-radius: 6px;
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  font-size: 14px;
  resize: vertical;
`;

const FieldInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldSummary = styled.div`
  cursor: pointer;
`;

const JsonEditorWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  padding: 16px;
  overflow: auto;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const AddSlideFieldFormWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  padding: 16px;
`;

const SaveButtonWrapper = styled.div`
  button {
    width: 420px;
  }
`;

const DeleteButtonWrapper = styled.div``;

const FORM_FIELD_KEYS = [
  'template_key',
  'question',
  'clarification',
  'search_terms',
  'answer_format',
  'heading',
  'agent_name',
  'id',
  'sequence_number',
  'num_query_runs',
  'query_max_answer_tokens',
  'query_max_context_tokens',
  'sub_key_mode',
];
const PRIMARY_FORM_FIELD_KEY_SET = new Set(['template_key', 'question']);
const ADVANCED_FORM_FIELD_KEYS = FORM_FIELD_KEYS.filter((key) => !PRIMARY_FORM_FIELD_KEY_SET.has(key));
const TEXTAREA_FIELD_KEYS = new Set(['question', 'clarification', 'search_terms']);
const MORE_FIELDS_SECTION_EXCLUDED_KEYS = new Set([
  'template_key',
  'question',
  'sub_key_items',
  '_subKeyItemsError',
  'options',
]);

const DEFAULT_SLIDE_FIELD = DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD;

const formatKeyName = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const getMoreSectionFieldKeys = (formField) => {
  const keys = Object.keys(formField || {}).filter((k) => !MORE_FIELDS_SECTION_EXCLUDED_KEYS.has(k));
  const knownOrdered = ADVANCED_FORM_FIELD_KEYS.filter((k) => keys.includes(k));
  const extra = keys.filter((k) => !knownOrdered.includes(k)).sort((a, b) => a.localeCompare(b));
  return [...knownOrdered, ...extra];
};

const getSlideFieldSummary = (slideField) => {
  return slideField?.question || slideField?.template_key || `Slide Field ${slideField?.id || ''}`.trim();
};

function ObjectFieldRow({ fieldKey, value, topSpacing, onChange }) {
  const [text, setText] = React.useState(() => JSON.stringify(value, null, 2));

  React.useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value, fieldKey]);

  const handleBlur = React.useCallback(() => {
    try {
      onChange(fieldKey, JSON.parse(text));
    } catch {
      setText(JSON.stringify(value, null, 2));
    }
  }, [fieldKey, onChange, text, value]);

  return (
    <Container top={topSpacing}>
      <FieldInputWrapper>
        <Body5 bold>{formatKeyName(fieldKey)}</Body5>
        <TextareaField value={text} onChange={(e) => setText(e.target.value)} onBlur={handleBlur} />
      </FieldInputWrapper>
    </Container>
  );
}

function SlideFieldInputs({ slideField, onChange, siblingTemplateKeys = [] }) {
  const [showMoreFields, setShowMoreFields] = React.useState(false);

  const handleSuggestKey = React.useCallback(() => {
    onChange('template_key', suggestNextTemplateKey([...siblingTemplateKeys, slideField.template_key ?? '']));
  }, [onChange, siblingTemplateKeys, slideField.template_key]);

  const renderFieldRow = (key, topSpacing) => {
    const raw = slideField[key];
    const displayValue = raw == null ? '' : typeof raw === 'boolean' ? (raw ? 'true' : 'false') : String(raw);

    return (
      <Container top={topSpacing} key={key}>
        <FieldInputWrapper>
          <Body5 bold>{formatKeyName(key)}</Body5>
          {TEXTAREA_FIELD_KEYS.has(key) ? (
            <TextareaField value={displayValue} onChange={(e) => onChange(key, e.target.value)} />
          ) : (
            <InputField value={displayValue} onChange={(e) => onChange(key, e.target.value)} />
          )}
        </FieldInputWrapper>
      </Container>
    );
  };

  const renderMoreSectionFieldRow = (key, topSpacing) => {
    const raw = slideField[key];

    if (raw !== null && typeof raw === 'object') {
      return <ObjectFieldRow key={key} fieldKey={key} value={raw} topSpacing={topSpacing} onChange={onChange} />;
    }

    return renderFieldRow(key, topSpacing);
  };

  return (
    <Container top={16}>
      <FieldInputWrapper>
        <Container row verticallyCenter spaceBetween fullWidth bottom={8}>
          <Body5 bold>{formatKeyName('template_key')}</Body5>
          <TransparentFlatButton onClick={handleSuggestKey}>Suggest key</TransparentFlatButton>
        </Container>
        <InputField value={slideField.template_key || ''} onChange={(e) => onChange('template_key', e.target.value)} />
      </FieldInputWrapper>
      {renderFieldRow('question', 16)}
      <Container top={16} row verticallyCenter gap={8}>
        <Container bottom={8}>
          <FieldSummary
            role="button"
            tabIndex={0}
            onClick={() => setShowMoreFields((value) => !value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowMoreFields((value) => !value);
              }
            }}
          >
            <Body5 bold uppercase>
              {showMoreFields ? 'Fewer Fields' : 'More Fields'}
            </Body5>
          </FieldSummary>
        </Container>
        <CollapseButton isCollapsed={!showMoreFields} setCollapsed={(collapsed) => setShowMoreFields(!collapsed)} />
      </Container>
      {showMoreFields && getMoreSectionFieldKeys(slideField).map((key) => renderMoreSectionFieldRow(key, 16))}
    </Container>
  );
}

function SubKeyItemsEditor({ slideField, onInitializeSubKeyItems, onClearSubKeyItems, onSubKeyItemsChange }) {
  return (
    <Container top={16}>
      <FieldInputWrapper>
        <Body5 bold>Sub Key Items</Body5>
        {slideField._subKeyItemsError && (
          <Container top={8}>
            <ErrorText>{slideField._subKeyItemsError}</ErrorText>
          </Container>
        )}
        {slideField.sub_key_items === null ? (
          <>
            <Container top={8}>
              <Body5>No JSON value set. This field will be saved as `null` until you initialize it.</Body5>
            </Container>
            <Container top={12}>
              <ActionsWrapper>
                <FlatButton onClick={onInitializeSubKeyItems}>Initialize JSON</FlatButton>
              </ActionsWrapper>
            </Container>
          </>
        ) : (
          <>
            <Container top={8}>
              <JsonEditorWrapper>
                <JsonEditor data={slideField.sub_key_items} setData={onSubKeyItemsChange} />
              </JsonEditorWrapper>
            </Container>
            <Container top={12}>
              <ActionsWrapper>
                <TransparentFlatButton onClick={onClearSubKeyItems}>Clear JSON</TransparentFlatButton>
              </ActionsWrapper>
            </Container>
          </>
        )}
      </FieldInputWrapper>
    </Container>
  );
}

function SlideFieldCardItem({ templateId, field, onUpdate, onDelete, siblingTemplateKeys = [] }) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [slideField, setSlideField] = React.useState(field);
  const [formError, setFormError] = React.useState(null);
  const [message, setMessage] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    setSlideField(field);
  }, [field]);

  const handleFieldChange = React.useCallback((key, value) => {
    setSlideField((currentField) => ({
      ...currentField,
      [key]: value,
    }));
  }, []);

  const handleSubKeyItemsChange = React.useCallback((value) => {
    setSlideField((currentField) => ({
      ...currentField,
      sub_key_items: value,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleInitializeSubKeyItems = React.useCallback(() => {
    setSlideField((currentField) => ({
      ...currentField,
      sub_key_items: {},
      _subKeyItemsError: null,
    }));
  }, []);

  const handleClearSubKeyItems = React.useCallback(() => {
    setSlideField((currentField) => ({
      ...currentField,
      sub_key_items: null,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleSave = async () => {
    setFormError(null);
    setMessage(null);
    setIsSaving(true);

    const { ok, data, error } = await updateRfpTemplateSlideField(templateId, field.id, slideField);

    setIsSaving(false);
    if (ok) {
      onUpdate(data?.id ? data : slideField);
      setMessage('Slide field saved successfully.');
      return;
    }

    setFormError(error || 'Something went wrong saving the slide field.');
  };

  const handleDelete = async () => {
    setFormError(null);
    setMessage(null);
    setIsDeleting(true);

    const { ok, error } = await deleteRfpTemplateSlideField(templateId, field.id);

    setIsDeleting(false);
    if (ok) {
      onDelete(field.id);
      return;
    }

    setFormError(error || 'Something went wrong deleting the slide field.');
  };

  return (
    <SlideFieldCard>
      <FieldSummary onClick={() => setIsCollapsed((currentValue) => !currentValue)}>
        <Body5 bold>{slideField.template_key || `Slide Field ${field.id}`}</Body5>
        <Container top={4}>
          <Body5>{getSlideFieldSummary(slideField)}</Body5>
        </Container>
      </FieldSummary>
      {!isCollapsed && (
        <>
          <SlideFieldInputs
            slideField={slideField}
            onChange={handleFieldChange}
            siblingTemplateKeys={siblingTemplateKeys}
          />
          <SubKeyItemsEditor
            slideField={slideField}
            onClearSubKeyItems={handleClearSubKeyItems}
            onInitializeSubKeyItems={handleInitializeSubKeyItems}
            onSubKeyItemsChange={handleSubKeyItemsChange}
          />
          <Container top={16}>
            {message && (
              <Container top={16}>
                <Body5 bold>{message}</Body5>
              </Container>
            )}
            {formError && (
              <Container top={16}>
                <ErrorText>{formError}</ErrorText>
              </Container>
            )}
            <Container top={16}>
              <ActionsWrapper>
                <SaveButtonWrapper>
                  <FlatButton
                    fullWidth
                    disabled={isSaving || isDeleting || Boolean(slideField._subKeyItemsError)}
                    onClick={handleSave}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </FlatButton>
                </SaveButtonWrapper>
                <DeleteButtonWrapper>
                  <TransparentFlatButton disabled={isSaving || isDeleting} onClick={handleDelete}>
                    {isDeleting ? 'Deleting...' : 'Delete Slide Field'}
                  </TransparentFlatButton>
                </DeleteButtonWrapper>
              </ActionsWrapper>
            </Container>
          </Container>
        </>
      )}
    </SlideFieldCard>
  );
}

function AddSlideFieldForm({ templateId, onComplete, onCancel, siblingTemplateKeys = [] }) {
  const [slideField, setSlideField] = React.useState(DEFAULT_SLIDE_FIELD);
  const [formError, setFormError] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const handleFieldChange = React.useCallback((key, value) => {
    setSlideField((currentField) => ({
      ...currentField,
      [key]: value,
    }));
  }, []);

  const handleSubKeyItemsChange = React.useCallback((value) => {
    setSlideField((currentField) => ({
      ...currentField,
      sub_key_items: value,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleInitializeSubKeyItems = React.useCallback(() => {
    setSlideField((currentField) => ({
      ...currentField,
      sub_key_items: {},
      _subKeyItemsError: null,
    }));
  }, []);

  const handleClearSubKeyItems = React.useCallback(() => {
    setSlideField((currentField) => ({
      ...currentField,
      sub_key_items: null,
      _subKeyItemsError: null,
    }));
  }, []);

  const handleSave = async () => {
    setFormError(null);
    setIsSaving(true);

    const { ok, data, error } = await createRfpTemplateSlideField(templateId, slideField);

    setIsSaving(false);
    if (ok) {
      onComplete(data);
      setSlideField(DEFAULT_SLIDE_FIELD);
      return;
    }

    setFormError(error || 'Something went wrong creating the slide field.');
  };

  return (
    <Container>
      <H5>Add Slide Field</H5>
      <SlideFieldInputs
        slideField={slideField}
        onChange={handleFieldChange}
        siblingTemplateKeys={siblingTemplateKeys}
      />
      <SubKeyItemsEditor
        slideField={slideField}
        onClearSubKeyItems={handleClearSubKeyItems}
        onInitializeSubKeyItems={handleInitializeSubKeyItems}
        onSubKeyItemsChange={handleSubKeyItemsChange}
      />
      {formError && (
        <Container top={16}>
          <ErrorText>{formError}</ErrorText>
        </Container>
      )}
      <Container top={16}>
        <ActionsWrapper>
          <FlatButton disabled={isSaving || Boolean(slideField._subKeyItemsError)} onClick={handleSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </FlatButton>
          <TransparentFlatButton disabled={isSaving} onClick={onCancel}>
            Cancel
          </TransparentFlatButton>
        </ActionsWrapper>
      </Container>
    </Container>
  );
}

export function SlideFieldsEditor({ template, onCancel }) {
  const [slideFields, setSlideFields] = React.useState([]);
  const [formError, setFormError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showAddForm, setShowAddForm] = React.useState(false);

  React.useEffect(() => {
    const cancelRef = { isCanceled: false };

    const loadSlideFields = async () => {
      setIsLoading(true);
      setFormError(null);

      const { ok, data, error } = await fetchRfpTemplateSlideFields(template.id);

      if (cancelRef.isCanceled) {
        return;
      }

      if (ok) {
        setSlideFields(Array.isArray(data) ? data : []);
      } else {
        setSlideFields([]);
        setFormError(error || 'Something went wrong loading slide fields.');
      }

      setIsLoading(false);
    };

    loadSlideFields();

    return () => {
      cancelRef.isCanceled = true;
    };
  }, [template.id]);

  const handleAddComplete = React.useCallback((slideField) => {
    setSlideFields((currentFields) => [...currentFields, slideField]);
    setShowAddForm(false);
  }, []);

  const handleUpdate = React.useCallback((updatedField) => {
    setSlideFields((currentFields) =>
      currentFields.map((field) => (field.id === updatedField.id ? updatedField : field))
    );
  }, []);

  const handleDelete = React.useCallback((fieldId) => {
    setSlideFields((currentFields) => currentFields.filter((field) => field.id !== fieldId));
  }, []);

  if (isLoading) {
    return (
      <Container top={24}>
        <Working message="Loading slide fields" />
      </Container>
    );
  }

  return (
    <Container top={24}>
      <Container row spaceBetween verticallyCenter fullWidth>
        <Body5 bold>Slide Fields</Body5>
        {!showAddForm && <FlatButton onClick={() => setShowAddForm(true)}>Add Slide Field</FlatButton>}
      </Container>
      {formError && (
        <Container top={16}>
          <ErrorText>{formError}</ErrorText>
        </Container>
      )}
      {showAddForm && (
        <Container top={16}>
          <AddSlideFieldFormWrapper>
            <AddSlideFieldForm
              templateId={template.id}
              onComplete={handleAddComplete}
              onCancel={() => setShowAddForm(false)}
              siblingTemplateKeys={slideFields.map((field) => field.template_key)}
            />
          </AddSlideFieldFormWrapper>
        </Container>
      )}
      <Container top={16}>
        <FlatButton fullWidth={false} onClick={onCancel}>
          Back To Actions
        </FlatButton>
      </Container>
      {slideFields.length === 0 && !formError ? (
        <Container top={16}>
          <Body5>No slide fields found for this template yet.</Body5>
        </Container>
      ) : (
        <Container top={16}>
          {slideFields.map((field, index) => (
            <Container top={index === 0 ? 0 : 16} key={field.id ?? `slide-field-${index}`}>
              <SlideFieldCardItem
                templateId={template.id}
                field={field}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                siblingTemplateKeys={slideFields
                  .filter((currentField) => currentField.id !== field.id)
                  .map((currentField) => currentField.template_key)}
              />
            </Container>
          ))}
        </Container>
      )}
    </Container>
  );
}
